import { useCallback, useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { Download, Loader2, RefreshCw, Video } from "lucide-react";
import { functions } from "../../firebase";
import { saveRecordingToDevice } from "./recordingDownload";

type RecordingStatus = "INVOKED" | "RECORDING" | "UPLOADING" | "UPLOADED" | "ERRORED" | "PAUSED";

type RecordingItem = {
    recordingId: string;
    roomId: string | null;
    title: string | null;
    status: RecordingStatus | null;
    fileSize: number | null;
    outputFileName: string | null;
    recordingDuration: number | null;
    startedTime: string | null;
    stoppedTime: string | null;
};

type DownloadResponse = {
    downloadUrl: string;
    outputFileName: string | null;
    fileSize: number | null;
};

const STATUS_LABEL: Record<string, string> = {
    INVOKED: "Preparando…",
    RECORDING: "Gravando…",
    UPLOADING: "Processando…",
    UPLOADED: "Pronta",
    ERRORED: "Falhou",
    PAUSED: "Pausada",
};

function formatDuration(seconds: number | null): string {
    if (!seconds || seconds <= 0) return "—";
    const total = Math.round(seconds);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) {
        return `${h}h ${String(m).padStart(2, "0")}min`;
    }
    return `${m}:${String(s).padStart(2, "0")}`;
}

function formatSize(bytes: number | null): string {
    if (!bytes || bytes <= 0) return "—";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb.toFixed(1)} MB`;
}

function formatDate(iso: string | null): string {
    if (!iso) return "—";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }) + " às " + date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

const RecordingsPanel: React.FC = () => {
    const [recordings, setRecordings] = useState<RecordingItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [downloadingId, setDownloadingId] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<{ id: string; message: string } | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage("");
        try {
            const listFn = httpsCallable<unknown, { recordings: RecordingItem[] }>(functions, "listMyRecordings");
            const result = await listFn();
            setRecordings(result.data?.recordings ?? []);
        } catch (error) {
            console.error("Erro ao carregar gravações:", error);
            setErrorMessage("Não foi possível carregar as gravações agora.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const handleDownload = useCallback(async (recording: RecordingItem) => {
        setDownloadingId(recording.recordingId);
        setFeedback(null);
        try {
            const getUrlFn = httpsCallable<{ recordingId: string }, DownloadResponse>(
                functions,
                "getRecordingDownloadUrl",
            );
            const result = await getUrlFn({ recordingId: recording.recordingId });
            const { downloadUrl, outputFileName, fileSize } = result.data;
            const fileName = outputFileName || recording.outputFileName || `gravacao-${recording.recordingId}.mp4`;
            const outcome = await saveRecordingToDevice(downloadUrl, fileName, fileSize ?? recording.fileSize);

            if (outcome === "opened") {
                setFeedback({
                    id: recording.recordingId,
                    message: "O vídeo abriu numa nova aba. Para salvar na galeria, use o botão de compartilhar/salvar dentro do player.",
                });
            } else if (outcome === "shared") {
                setFeedback({ id: recording.recordingId, message: "Escolha \"Salvar vídeo\" na tela que abriu para guardar na galeria." });
            }
        } catch (error: any) {
            console.error("Erro ao baixar gravação:", error);
            const message =
                error?.code === "functions/failed-precondition"
                    ? "A gravação ainda está sendo processada. Tente novamente em alguns minutos."
                    : "Não foi possível baixar a gravação agora.";
            setFeedback({ id: recording.recordingId, message });
        } finally {
            setDownloadingId(null);
        }
    }, []);

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Gravações</h2>
                <button
                    type="button"
                    onClick={() => void load()}
                    className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    disabled={isLoading}
                >
                    <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
                    Atualizar
                </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
                Gravações feitas nas suas salas. No computador, o botão baixa o arquivo direto.
                No celular, toque em "Salvar" na tela que abrir para guardar na galeria.
            </p>

            {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    {errorMessage}
                </div>
            )}

            {isLoading ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
            ) : recordings.length === 0 ? (
                <div className="text-center py-10">
                    <Video className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Nenhuma gravação ainda.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {recordings.map((recording) => {
                        const isReady = recording.status === "UPLOADED";
                        const isBusy = downloadingId === recording.recordingId;
                        return (
                            <div
                                key={recording.recordingId}
                                className="flex items-center justify-between gap-4 p-4 border border-gray-200 rounded-lg"
                            >
                                <div className="min-w-0">
                                    <p className="font-medium text-gray-800 truncate">
                                        {recording.title || recording.roomId || "Sessão"}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        {formatDate(recording.stoppedTime || recording.startedTime)} ·{" "}
                                        {formatDuration(recording.recordingDuration)} ·{" "}
                                        {formatSize(recording.fileSize)}
                                    </p>
                                    {feedback?.id === recording.recordingId && (
                                        <p className="text-xs text-blue-600 mt-1">{feedback.message}</p>
                                    )}
                                </div>
                                <div className="flex-shrink-0">
                                    {isReady ? (
                                        <button
                                            type="button"
                                            onClick={() => void handleDownload(recording)}
                                            disabled={isBusy}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-60"
                                        >
                                            {isBusy ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Download className="w-4 h-4" />
                                            )}
                                            Baixar
                                        </button>
                                    ) : (
                                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                                            {STATUS_LABEL[recording.status || ""] || "—"}
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RecordingsPanel;
